// src/components/FeedbackModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

const REASONS = [
    { value: "EXISTING_SYSCO_ACCOUNT", label: "This is an existing Sysco account" },
    { value: "INCORRECT_INFORMATION", label: "Incorrect Information" },
    { value: "MISSING_INFORMATION", label: "Missing Information" },
    { value: "NO_LONGER_OPERATIONAL", label: "This location no longer operational" },
    { value: "OTHER", label: "Other" },
];

const INCORRECT_FIELDS = [
    { value: "LOCATION_ADDRESS", label: "Location/Address" },
    { value: "PLACE_NAME", label: "Place Name" },
    { value: "OPERATION_HOURS", label: "Operation Hours" },
];

function getCommentPlaceholder(reason) {
    if (!reason) return "";
    if (reason === "EXISTING_SYSCO_ACCOUNT") return "Provide corresponding Account name / Account number";
    if (reason === "INCORRECT_INFORMATION") return "Submit corrected information";
    if (reason === "MISSING_INFORMATION") return "Give additional information";
    if (reason === "NO_LONGER_OPERATIONAL") return "Additional notes";
    return "Add details";
}

export default function FeedbackModal({
    isOpen,
    onClose,
    onSubmit,
    title = "Give Feedback",
    isMobileView = false,
}) {
    const [reason, setReason] = useState("");
    const [incorrectFields, setIncorrectFields] = useState([]);
    const [comment, setComment] = useState("");

    // Reset when opening
    useEffect(() => {
        if (!isOpen) return;
        setReason("");
        setIncorrectFields([]);
        setComment("");
    }, [isOpen]);

    // Clear incorrectFields if reason changes away
    useEffect(() => {
        if (reason !== "INCORRECT_INFORMATION") setIncorrectFields([]);
    }, [reason]);

    const placeholder = useMemo(() => getCommentPlaceholder(reason), [reason]);

    const isValid = useMemo(() => {
        if (!reason) return false;
        if (reason === "INCORRECT_INFORMATION" && incorrectFields.length === 0) return false;
        return comment.trim().length > 0;
    }, [reason, incorrectFields, comment]);

    const toggleIncorrectField = (val) => {
        setIncorrectFields((prev) =>
            prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val]
        );
    };

    const handleSubmit = () => {
        if (!isValid) return;

        const payload = {
            reason,
            comment: comment.trim(),
            incorrectFields: reason === "INCORRECT_INFORMATION" ? incorrectFields : [],
        };

        console.log("[feedback] modal submit payload", payload);
        onSubmit?.(payload);
    };

    // lock body scroll while open (helps on mobile + desktop)
    useEffect(() => {
        if (!isOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [isOpen]);

    if (!isOpen) return null;

    // IMPORTANT: render into document.body so "fixed" is truly viewport-fixed
    const modal = (
        <div className="fixed inset-0 z-[9999]">
            {/* backdrop */}
            <div
                className="absolute inset-0 bg-black/40"
                onClick={isMobileView ? undefined : onClose}
                aria-hidden="true"
            />

            {/* dialog */}
            <div
                role="dialog"
                aria-modal="true"
                aria-label={title}
                className={
                    isMobileView
                        ? // Fullscreen sheet
                        "absolute inset-0 bg-white flex flex-col"
                        : // Centered modal with internal scroll (no cut-off)
                        "absolute left-1/2 top-16 w-[92%] max-w-lg -translate-x-1/2 bg-white shadow-lg rounded-lg flex flex-col max-h-[90vh]"
                }
            >
                {/* header */}
                {isMobileView ? (

                    <>
                        {/* Blue header */}
                        <div className="relative bg-sky-600 text-white px-4 pt-4 pb-6">
                            <div className="flex items-start justify-between">
                                <div className="text-fluid-xl font-bold">
                                    Feedback
                                </div>

                                <button
                                    type="button"
                                    className="p-2 rounded-full hover:bg-white/10"
                                    onClick={onClose}
                                    aria-label="Close feedback"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>

                        {/* White curved overlap (THIS creates the curve) */}
                        <div className="-mt-4 bg-white rounded-t-2xl px-4 py-3 shadow-sm relative z-10">
                            <p className="text-sm text-gray-700">
                                Please select a reason and add details if needed.
                            </p>
                        </div>
                    </>

                ) : (
                    // Desktop header (keep existing)
                    <div className="flex items-center justify-between border-b bg-white px-4 py-3">
                        <div className="text-base font-semibold">{title}</div>
                        <button
                            type="button"
                            className="px-2 py-1 text-sm"
                            onClick={onClose}
                            aria-label="Close feedback"
                        >
                            ✕
                        </button>
                    </div>
                )}

                {/* body (SCROLLS) */}
                <div className="flex-1 overflow-y-auto px-4 py-3">
                    <div className="text-sm font-semibold mb-2">
                        Reason <span className="text-red-600">*</span>
                    </div>

                    <div className="space-y-2">
                        {REASONS.map((r) => (
                            <label key={r.value} className="flex items-center gap-2 text-sm">
                                <input
                                    type="radio"
                                    name="feedback-reason"
                                    value={r.value}
                                    checked={reason === r.value}
                                    onChange={() => setReason(r.value)}
                                />
                                <span>{r.label}</span>
                            </label>
                        ))}
                    </div>

                    {reason === "INCORRECT_INFORMATION" && (
                        <div className="mt-4">
                            <div className="text-sm font-semibold mb-2">
                                Incorrect Information (select all that apply){" "}
                                <span className="text-red-600">*</span>
                            </div>

                            <div className="space-y-2">
                                {INCORRECT_FIELDS.map((f) => (
                                    <label key={f.value} className="flex items-center gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={incorrectFields.includes(f.value)}
                                            onChange={() => toggleIncorrectField(f.value)}
                                        />
                                        <span>{f.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {reason && (
                        <div className="mt-4">
                            <div className="text-sm font-semibold mb-2">
                                Comment <span className="text-red-600">*</span>
                            </div>

                            <textarea
                                className="w-full rounded-md border p-2 text-sm"
                                rows={isMobileView ? 7 : 4}
                                placeholder={placeholder}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                            />
                        </div>
                    )}
                </div>

                {/* footer */}
                <div className="flex items-center justify-end gap-2 border-t bg-white px-4 py-3">
                    <button type="button" className="btn-pill btn-pill-xs" onClick={onClose}>
                        Cancel
                    </button>

                    <button
                        type="button"
                        className="btn-pill btn-pill-xs pill-blue-sel"
                        disabled={!isValid}
                        onClick={handleSubmit}
                        title={!isValid ? "Select reason and enter comment" : ""}
                    >
                        Submit
                    </button>
                </div>
            </div>
        </div>
    );

    return createPortal(modal, document.body);
}